import * as PIXI from 'pixi.js';
import earcut from 'earcut';
import { Settings } from '../../Settings';
import { getCharacterBloodColors } from '../BloodColors';
import { DEFAULT_BLOOD_COLOR, rainbowBloodColor } from '../../../static/mod/bloodColors';
import { clipToRect } from './goreGeometry';
import { PALETTE } from './goreShapes';

const arts = new Map();

export const bloodOf = body => {
	const color = Settings.bloodEnabled ? getCharacterBloodColors(body.bloodSkin).start : rainbowBloodColor(performance.now() / 1000);
	return color === DEFAULT_BLOOD_COLOR ? PALETTE.meatBright : parseInt(color.slice(1), 16);
};

export const css = color => '#' + color.toString(16).padStart(6, '0');

export const makeCanvas = (w, h) => {
	const canvas = document.createElement('canvas');
	canvas.width = w;
	canvas.height = h;
	return canvas;
};

export const drawTexture = (ctx, texture) => {
	const source = texture.source;
	const resolution = source.resolution || 1;
	const {frame, trim} = texture;
	ctx.drawImage(source.resource,
		frame.x * resolution, frame.y * resolution, frame.width * resolution, frame.height * resolution,
		trim ? trim.x : 0, trim ? trim.y : 0, frame.width, frame.height);
};

// Mods swap the image inside a source (ModManager.applyModSource), so a read
// only holds for the image it was read from.
export const artOf = texture => {
	const {frame} = texture;
	const key = `${texture.source.uid}:${frame.x},${frame.y},${frame.width},${frame.height}`;
	const cached = arts.get(key);
	if (cached && cached.resource === texture.source.resource) return cached.failed ? null : cached;
	let art = null;
	try {
		if (!texture.rotate) {
			const w = Math.round(texture.orig.width);
			const h = Math.round(texture.orig.height);
			const canvas = makeCanvas(w, h);
			const ctx = canvas.getContext('2d', {willReadFrequently: true});
			drawTexture(ctx, texture);
			const rgba = ctx.getImageData(0, 0, w, h).data;
			const index = (x, y) => {
				const ix = Math.floor(x);
				const iy = Math.floor(y);
				return ix < 0 || iy < 0 || ix >= w || iy >= h ? -1 : (iy * w + ix) * 4;
			};
			art = {
				w, h, rgba, canvas, bone: undefined, resource: texture.source.resource,
				alphaAt: (x, y) => {
					const i = index(x, y);
					return i < 0 ? 0 : rgba[i + 3] / 255;
				},
				colorAt: (x, y) => {
					const i = index(x, y);
					return i < 0 ? 0 : (rgba[i] << 16) | (rgba[i + 1] << 8) | rgba[i + 2];
				},
			};
		}
	} catch (error) {
		art = null;
	}
	arts.set(key, art || {resource: texture.source.resource, failed: true});
	return art;
};

export const tracePath = (ctx, poly) => {
	ctx.moveTo(poly[0].x, poly[0].y);
	for (let i = 1; i < poly.length; i++) ctx.lineTo(poly[i].x, poly[i].y);
	ctx.closePath();
};

export const paint = (ctx, shapes) => {
	for (const shape of shapes) {
		ctx.globalAlpha = shape.alpha ?? 1;
		ctx.beginPath();
		if (shape.type === 'line') {
			const points = shape.points;
			ctx.moveTo(points[0].x, points[0].y);
			for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
			ctx.lineWidth = shape.width;
			ctx.strokeStyle = css(shape.color);
			ctx.stroke();
			continue;
		}
		if (shape.type === 'circle') ctx.arc(shape.x, shape.y, shape.r, 0, Math.PI * 2);
		else if (shape.type === 'ellipse') ctx.ellipse(shape.x, shape.y, shape.rx, shape.ry, shape.rotation, 0, Math.PI * 2);
		else tracePath(ctx, shape.points);
		ctx.fillStyle = css(shape.fill);
		ctx.fill();
		if (shape.stroke !== undefined) {
			ctx.lineWidth = shape.strokeWidth;
			ctx.strokeStyle = css(shape.stroke);
			ctx.stroke();
		}
	}
	ctx.globalAlpha = 1;
};

export const shapeBounds = (shapes, box = {minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity}) => {
	const add = (x, y, pad) => {
		box.minX = Math.min(box.minX, x - pad); box.minY = Math.min(box.minY, y - pad);
		box.maxX = Math.max(box.maxX, x + pad); box.maxY = Math.max(box.maxY, y + pad);
	};
	for (const shape of shapes) {
		if (shape.points) for (const p of shape.points) add(p.x, p.y, (shape.width || shape.strokeWidth || 0) / 2 + 0.5);
		else add(shape.x, shape.y, (shape.r ?? Math.max(shape.rx, shape.ry)) + (shape.strokeWidth || 0) / 2 + 0.5);
	}
	return box;
};

export const extrasOf = skin => skin.children.filter(child => child !== skin.originalSprite && child !== skin.myFlesh &&
	child instanceof PIXI.Sprite && child.visible && !child.destroyed && !child.texture.rotate && child.texture.source?.resource);

export const drawExtras = (ctx, extras) => {
	for (const child of extras) {
		const texture = child.texture;
		ctx.save();
		ctx.translate(child.x, child.y);
		ctx.rotate(child.rotation);
		ctx.scale(child.scale.x, child.scale.y);
		ctx.translate(-child.anchor.x * texture.orig.width, -child.anchor.y * texture.orig.height);
		drawTexture(ctx, texture);
		ctx.restore();
	}
};

// `poly` is in the space where the texture's untrimmed frame starts at (x, y).
export const meshOf = (poly, texture, tint = 0xffffff, x = 0, y = 0) => {
	const {frame, trim} = texture;
	const x0 = x + (trim ? trim.x : 0);
	const y0 = y + (trim ? trim.y : 0);
	const clipped = poly && clipToRect(poly, x0, y0, x0 + frame.width, y0 + frame.height);
	if (!clipped) return null;
	const flat = [];
	for (const v of clipped) flat.push(v.x, v.y);
	const indices = earcut(flat);
	if (!indices.length) return null;
	const uvs = new Float32Array(flat.length);
	for (let i = 0; i < flat.length; i += 2) {
		uvs[i] = (flat[i] - x0) / frame.width;
		uvs[i + 1] = (flat[i + 1] - y0) / frame.height;
	}
	const geometry = new PIXI.MeshGeometry({positions: new Float32Array(flat), uvs, indices: new Uint32Array(indices)});
	geometry.batchMode = 'batch';
	const mesh = new PIXI.Mesh({geometry, texture});
	mesh.tint = tint;
	return mesh;
};

export const destroyMesh = mesh => {
	if (!mesh || mesh.destroyed) return;
	const geometry = mesh.geometry;
	mesh.destroy();
	geometry.destroy();
};
